import { all, takeLatest, call, select, put, getContext, cancel } from "redux-saga/effects";
import { isDefined } from "@grandvisionhq/utils";
import { isStartConfiguratorWithId, isNextStepWithId, isPreviousStepWithId, isNextStepSuccessWithId, isPreviousStepSuccessWithId, isFinishWithId, nextStep, stepShown, nextStepFailed, finish, nextStepSuccess, previousStepFailed, previousStepSuccess, closeFooter as closeFooter$1, finishSuccess, finishFailed } from "./actions.js";
import { getCurrentStep } from "./selectors.js";
import { getStepsList, getActiveSteps, getCurrentStepNumber } from "./selectors.internal.js";
import { isDynamicStep } from "./state.js";
function logUndefinedStep(configuratorId, currentStep) {
  console.warn(
    `Could not determine the current step of configurator with ID "${configuratorId}". Received ${currentStep}`
  );
}
function logUndefinedStepProperty(configuratorId, propertyName, propertyValue) {
  console.warn(
    `Could not determine property "${propertyName}" of the current step in configurator with ID "${configuratorId}". Received ${propertyValue}`
  );
}
function* getConfiguratorContext(id) {
  const configuratorSagaContext = yield getContext(id);
  if (!isDefined(configuratorSagaContext)) {
    console.warn(
      `Could not find the saga context of configurator with ID "${id}". Received ${configuratorSagaContext}`
    );
    return yield cancel();
  }
  const stepsMap = configuratorSagaContext.stepsMap;
  const stepSequence = configuratorSagaContext.stepSequence;
  if (!isDefined(stepsMap)) {
    logUndefinedStepProperty(id, "stepsMap", stepsMap);
    return yield cancel();
  }
  if (!isDefined(stepSequence)) {
    logUndefinedStepProperty(id, "stepSequence", stepSequence);
    return yield cancel();
  }
  return configuratorSagaContext;
}
function* onNextStep({ payload: { id } }) {
  const configuratorSagaContext = yield call(getConfiguratorContext, id);
  const { stepsMap, stepSequence } = configuratorSagaContext;
  const activeSteps = yield select(getActiveSteps, { id, stepsMap, stepSequence });
  const currentStep = yield select(getCurrentStep, { id });
  if (!isDefined(currentStep)) {
    logUndefinedStep(id, currentStep);
    return;
  }
  const currentStepNumber = yield select(
    getCurrentStepNumber,
    { id }
  );
  if (!isDefined(currentStepNumber)) {
    logUndefinedStepProperty(id, "currentStepNumber", currentStepNumber);
    return;
  }
  const step = stepsMap?.[currentStep.name];
  if (!isDefined(step)) {
    console.error(
      `Could not find step data for step with name "${currentStep.name}" in configurator with ID "${id}".`
    );
    return;
  }
  if (step.onNext) {
    const result = yield call(step.onNext);
    if (result) {
      yield put(nextStepFailed({ id, currentStepNumber, reason: result }));
      return;
    }
  }
  const nextActiveStepName = activeSteps.reduce(
    (stepName, curr, index) => curr.isCurrent ? activeSteps[index + 1]?.name : stepName,
    void 0
  );
  if (!nextActiveStepName) {
    yield put(finish({ id }));
    return;
  }
  if (!isDefined(stepSequence)) {
    return;
  }
  const nextActiveStepIndex = stepSequence.indexOf(nextActiveStepName);
  yield put(nextStepSuccess({ id, currentStepNumber, newStep: nextActiveStepIndex }));
}
function* triggerOnEnterForFirstStep({
  payload: { id, stepSequence }
}) {
  const configuratorSagaContext = yield call(getConfiguratorContext, id);
  const { stepsMap } = configuratorSagaContext;
  const steps = yield select(getStepsList, { id, stepsMap, stepSequence });
  const currentStepName = steps[0]?.name;
  if (!currentStepName) {
    logUndefinedStepProperty(id, "name", currentStepName);
    return;
  }
  const step = stepsMap[currentStepName];
  if (!isDefined(step)) {
    logUndefinedStepProperty(id, "name", step);
    return;
  }
  const stepStatus = isDynamicStep(step) ? yield select(step.stepStatus) : yield select(() => "active");
  if (stepStatus !== "active") {
    yield put(nextStep({ id }));
    return;
  }
  if (step && step.onEnter) {
    yield call(step.onEnter);
  }
  yield put(stepShown({ id, step: currentStepName }));
}
function* onSuccessfulStepSwitch({
  payload: { id, newStep }
}) {
  const configuratorSagaContext = yield call(getConfiguratorContext, id);
  const { stepsMap, stepSequence } = configuratorSagaContext;
  if (!isDefined(stepSequence)) {
    logUndefinedStepProperty(id, "stepSequence", stepSequence);
    return;
  }
  const stepName = newStep < stepSequence.length ? stepSequence[newStep] : void 0;
  if (!stepName) {
    logUndefinedStep(id, stepName);
    return;
  }
  const step = stepsMap?.[stepName];
  if (!isDefined(step)) {
    logUndefinedStep(id, step);
    return;
  }
  if (step.onEnter) {
    yield call(step.onEnter);
  }
  yield put(stepShown({ id, step: stepName }));
}
function* onPreviousStep({ payload: { id } }) {
  const configuratorSagaContext = yield call(getConfiguratorContext, id);
  const { stepsMap, stepSequence } = configuratorSagaContext;
  const activeSteps = yield select(getActiveSteps, { id, stepsMap, stepSequence });
  const currentStepNumber = yield select(
    getCurrentStepNumber,
    { id }
  );
  if (!isDefined(currentStepNumber)) {
    logUndefinedStepProperty(id, "currentStepNumber", currentStepNumber);
    return;
  }
  const previousActiveStepName = activeSteps.reduce(
    (stepName, curr, index) => curr.isCurrent ? activeSteps[index - 1]?.name : stepName,
    void 0
  );
  if (!previousActiveStepName) {
    yield put(
      previousStepFailed({
        id,
        currentStepNumber,
        reason: new Error(`Can't go back beyond the first step`)
      })
    );
    return;
  }
  if (!isDefined(stepSequence)) {
    logUndefinedStepProperty(id, "stepSequence", stepSequence);
    return;
  }
  const previousActiveStepIndex = stepSequence.indexOf(previousActiveStepName);
  yield put(
    previousStepSuccess({ id, currentStepNumber, newStep: previousActiveStepIndex })
  );
}
function* closeFooter({ payload: { id } }) {
  yield put(closeFooter$1({ id }));
}
function* triggerOnFinishForLastStep({ payload: { id } }) {
  const configuratorSagaContext = yield call(getConfiguratorContext, id);
  const onFinish = configuratorSagaContext?.onFinish;
  if (!onFinish) {
    yield put(finishSuccess({ id }));
  }
  try {
    yield call(onFinish);
  } catch (error) {
    yield put(finishFailed({ id, reason: error }));
  }
}
function configuratorFactory(configuratorNamespace) {
  return {
    rootSaga: function* () {
      yield all([
        takeLatest(
          isStartConfiguratorWithId(configuratorNamespace),
          triggerOnEnterForFirstStep
        ),
        takeLatest(isNextStepWithId(configuratorNamespace), onNextStep),
        takeLatest(isPreviousStepWithId(configuratorNamespace), onPreviousStep),
        takeLatest(
          [
            isNextStepWithId(configuratorNamespace),
            isPreviousStepWithId(configuratorNamespace)
          ],
          closeFooter
        ),
        takeLatest(
          [
            isNextStepSuccessWithId(configuratorNamespace),
            isPreviousStepSuccessWithId(configuratorNamespace)
          ],
          onSuccessfulStepSwitch
        ),
        takeLatest(isFinishWithId(configuratorNamespace), triggerOnFinishForLastStep)
      ]);
    }
  };
}
export {
  configuratorFactory,
  getConfiguratorContext
};
//# sourceMappingURL=sagas.js.map
